/**
 * ResetPasswordPage — WO5 user-data isolation batch.
 *
 * Reads ?token=... from the URL. Two password fields (new + confirm).
 * On success: redirects to /login with a success banner via location state.
 * On error: shows the generic "Reset link is invalid or expired." message
 * (mirrors the backend's INVALID_OR_EXPIRED_TOKEN contract — never reveal
 * whether the token was wrong vs expired vs used).
 *
 * Weak-password is shown distinctly because the user CAN fix that without
 * needing a new link.
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { apiV1Base } from '../lib/apiConfig';

const GENERIC_INVALID = 'Reset link is invalid or expired.';
const WEAK_PASSWORD_HINT =
  'Password must be at least 8 characters and include at least one letter and one digit.';

function _validatePolicy(pw: string): boolean {
  if (!pw || pw.length < 8) return false;
  if (!/[A-Za-z]/.test(pw)) return false;
  if (!/\d/.test(pw)) return false;
  return true;
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = (params.get('token') || '').trim();
    if (!t) {
      setTokenMissing(true);
      return;
    }
    setToken(t);
  }, [location.search]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    if (!_validatePolicy(newPassword)) {
      setErrorMsg(WEAK_PASSWORD_HINT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const r = await fetch(`${apiV1Base()}/auth/password-reset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      if (r.ok) {
        navigate('/login', {
          state: { passwordResetSuccess: true },
          replace: true,
        });
        return;
      }
      let code = '';
      try {
        const body = await r.json();
        code = (body && (body.error as string)) || '';
      } catch {
        code = '';
      }
      if (code === 'WEAK_PASSWORD') {
        setErrorMsg(WEAK_PASSWORD_HINT);
      } else {
        // INVALID_OR_EXPIRED_TOKEN or anything else → generic copy.
        setErrorMsg(GENERIC_INVALID);
      }
    } catch {
      setErrorMsg(GENERIC_INVALID);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        background: 'var(--app-bg, #07070b)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--card-bg, #111118)',
          border: '1px solid var(--card-border, #1f1f2c)',
          borderRadius: 14,
          padding: '1.75rem',
          color: 'var(--app-text, #e2e2e8)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            margin: '0 0 0.4rem 0',
            letterSpacing: '-0.01em',
          }}
        >
          Set a new password
        </h1>
        <p
          style={{
            fontSize: 13,
            color: 'var(--muted-text, #6b6b80)',
            margin: '0 0 1.5rem 0',
            lineHeight: 1.5,
          }}
        >
          {WEAK_PASSWORD_HINT}
        </p>

        {tokenMissing ? (
          <div
            data-testid="reset-password-missing-token"
            style={{
              padding: '0.9rem 1rem',
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.5,
              color: '#fda4a4',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              {GENERIC_INVALID}{' '}
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#10b981',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Request a new link.
              </button>
            </span>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* New password */}
            <label
              style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--muted-text, #6b6b80)',
                marginBottom: 6,
              }}
            >
              New password
            </label>
            <div style={{ position: 'relative', marginBottom: '1rem' }}>
              <Lock
                size={14}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted-text, #6b6b80)',
                }}
              />
              <input
                data-testid="reset-password-new"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 chars, letter + digit"
                style={{
                  width: '100%',
                  padding: '0.7rem 0.85rem 0.7rem 2.2rem',
                  fontSize: 14,
                  background: 'var(--input-bg, #0d0d14)',
                  border: '1px solid var(--input-border, #25253a)',
                  borderRadius: 8,
                  color: 'var(--app-text, #e2e2e8)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Confirm */}
            <label
              style={{
                display: 'block',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--muted-text, #6b6b80)',
                marginBottom: 6,
              }}
            >
              Confirm new password
            </label>
            <div style={{ position: 'relative', marginBottom: '1.25rem' }}>
              <Lock
                size={14}
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted-text, #6b6b80)',
                }}
              />
              <input
                data-testid="reset-password-confirm"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                style={{
                  width: '100%',
                  padding: '0.7rem 0.85rem 0.7rem 2.2rem',
                  fontSize: 14,
                  background: 'var(--input-bg, #0d0d14)',
                  border: '1px solid var(--input-border, #25253a)',
                  borderRadius: 8,
                  color: 'var(--app-text, #e2e2e8)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {errorMsg && (
              <div
                data-testid="reset-password-error"
                style={{
                  marginBottom: '1rem',
                  padding: '0.7rem 0.85rem',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 8,
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: '#fda4a4',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                }}
              >
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="reset-password-submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.7rem 1rem',
                fontSize: 13,
                fontWeight: 800,
                background: '#10b981',
                color: '#06170f',
                border: 'none',
                borderRadius: 8,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: loading ? 0.8 : 1,
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving new password...
                </>
              ) : (
                <>
                  <ShieldCheck size={14} />
                  Save new password
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
